import { PagingInfo } from "src/app/ui/models/domains/paginator/paging-info.model";
import { JobDescription } from "src/app/ui/models/domains/pbm/Beneficiary/job-description";
import { Address } from "src/app/ui/models/domains/pbm/companies/address";
import { Company } from "src/app/ui/models/domains/pbm/companies/company";
import { CompanyContact } from "src/app/ui/models/domains/pbm/companies/company-contact";
import { CompanyFinancialReportsPageableResult } from "src/app/ui/models/domains/pbm/companies/company-financial-reports-pageable-result.model";
import { CompanyFinancialReports } from "src/app/ui/models/domains/pbm/companies/company-financial-reports.model";
import { CompanyInfo } from "src/app/ui/models/domains/pbm/companies/company-info";
import { CompanyKey } from "src/app/ui/models/domains/pbm/companies/company-key";
import {
  AddressMasterData,
  CompanyFederalOfficerActivityInfo,
  CompanyFederalOfficerBillingInfo,
  CompanyFederalOfficerSocialPartnerInfo,
  CompanyMasterData,
  CompanyMasterDataInfo,
  FederalOfficerInfo,
  GeoLocation,
} from "src/app/ui/models/domains/pbm/companies/company-master-data";
import { CompanyRemainingLimit } from "src/app/ui/models/domains/pbm/companies/company-remaining-limit";
import { FederalRegistration } from "src/app/ui/models/domains/pbm/companies/federal-registration";
import { SocialNumber } from "src/app/ui/models/domains/pbm/companies/social-number";

export function parseCompany(payload: any): Company {
  const company = new Company();
  company.setCompanyKey(parseCompanyKey(payload.CompanyKey != undefined ? payload.CompanyKey : payload.companyKey));
  company.setFiscalNumber(payload.FiscalNumber != undefined ? payload.FiscalNumber : payload.fiscalNumber);
  company.setHeadOfficeFiscalNumber(payload.HeadOfficeFiscalNumber != undefined ? payload.HeadOfficeFiscalNumber : payload.headOfficeFiscalNumber);
  company.setCompanyType(payload.CompanyType != undefined ? payload.CompanyType : payload.companyType);
  company.setCorporateName(payload.CorporateName != undefined ? payload.CorporateName : payload.corporateName);
  company.setTradingName(payload.TradingName != undefined ? payload.TradingName : payload.tradingName);
  company.setUnit(payload.Unit != undefined ? payload.Unit : payload.unit);
  company.setStateRegistration(payload.StateRegistration != undefined ? payload.StateRegistration : payload.stateRegistration);
  company.setMunicipalRegistration(payload.MunicipalRegistration != undefined ? payload.MunicipalRegistration : payload.municipalRegistration);
  company.setCompanyContacts(parseCompanyContactList(payload.CompanyContacts != undefined ? payload.CompanyContacts : payload.companyContacts));
  company.setCompanyInfo(parseCompanyInfo(payload.CompanyInfo != undefined ? payload.CompanyInfo : payload.companyInfo));
  company.setIsActive(payload.IsActive);
  return company;
}

export function parseCompanyKey(payload: any): CompanyKey {
  const companyKey = new CompanyKey();
  if (payload) {
    companyKey.setCompanyKey(payload.Value);
  }
  return companyKey;
}

export function parseCompanyContactList(payload): CompanyContact[] {
  const companyContacts: CompanyContact[] = [];
  if (payload === null || payload == undefined) {
    return companyContacts;
  } else {
    payload.forEach((contact) => {
      const companyContact = new CompanyContact();
      companyContact.setCompanyContactKey(contact.CompanyContactKey);
      companyContact.setCompanyFiscalNumber(contact.CompanyFiscalNumber);
      companyContact.setName(contact.Name);
      companyContact.setUnit(contact.Unit);
      companyContact.setJobDescription(parseJobDescription(contact.JobDescription));
      companyContact.setTelephone(contact.Telephone);
      companyContact.setMobile(contact.Mobile);
      companyContact.setEmail(contact.Email);
      companyContact.setArea(contact.Area);
      companyContact.setHasLegalRepresentativeInfo(contact.HasLegalRepresentativeInfo);
      companyContact.setAdditionalInformation(contact.AdditionalInformation);
      if (contact.FederalRegistration) {
        companyContact.setFederalRegistration(new FederalRegistration(contact.FederalRegistration.Value));
      } else {
        companyContact.setFederalRegistration(new FederalRegistration(""));
      }
      if (contact.SocialNumber) {
        companyContact.setSocialNumber(new SocialNumber(contact.SocialNumber.Value));
      } else {
        companyContact.setSocialNumber(new SocialNumber(""));
      }
      companyContacts.push(companyContact);
    });
    return companyContacts;
  }
}

export function parseCompanyFinancialReportsPageableResult(payload): CompanyFinancialReportsPageableResult {
  const companyFinancialReportsPageableResult = new CompanyFinancialReportsPageableResult();

  if (payload) {
    const pagingInfo = new PagingInfo();
    pagingInfo.setStart(payload.Start);
    pagingInfo.setEnd(payload.Start);

    companyFinancialReportsPageableResult.setPagingInfo(pagingInfo);
    companyFinancialReportsPageableResult.setStart(payload.Start);
    companyFinancialReportsPageableResult.setTotal(payload.Total);
    companyFinancialReportsPageableResult.setTotalPerPages(payload.TotalPerPages);
    companyFinancialReportsPageableResult.setData(parseCompanyFinancialReports(payload.Data));
  }

  return companyFinancialReportsPageableResult;
}

export function parseCompanyFinancialReports(payload): CompanyFinancialReports[] {
  const companyFinancialReports: CompanyFinancialReports[] = [];

  if (payload) {
    payload.forEach((data) => {
      const companyFinancialReport = new CompanyFinancialReports();

      companyFinancialReport.setCompanyFinancialReportKey(data.CompanyFinancialReportKey.Value);
      companyFinancialReport.setCycleBalanceEndDate(data.CycleBalanceEndDate);
      companyFinancialReport.setCycleBalanceStartDate(data.CycleBalanceStartDate);
      companyFinancialReport.setCompanyName(data.CompanyName);
      companyFinancialReport.setDownloadUrl(data.DownloadUrl);
      companyFinancialReport.setEmployeeCount(data.EmployeeCount);
      companyFinancialReport.setFiscalNumber(data.FiscalNumber.Value);
      companyFinancialReport.setOperationDate(data.OperationDate);

      companyFinancialReports.push(companyFinancialReport);
    });
  }

  return companyFinancialReports;
}

export function parseCompanyInfo(payload: any): CompanyInfo {
  const companyInfo = new CompanyInfo();
  if (payload) {
    companyInfo.setAddress(parseAddress(payload.Address));
    companyInfo.setEmployeeCount(payload.EmployeeCount);
  }
  return companyInfo;
}

export function parseAddress(payload: any): Address {
  const address = new Address();
  if (payload) {
    address.setIsNewAddress(payload.IsNewAddress);
    address.setStreet(payload.Street);
    address.setNumber(payload.Number);
    address.setComplement(payload.Complement);
    address.setZipCode(payload.ZipCode ? payload.ZipCode : "");
    address.setDistrict(payload.District);
    address.setCity(payload.City ? payload.City.trim() : "");
    address.setRegion(payload.Region ? payload.Region.trim() : "");
  }
  return address;
}

export function parseCompanyMasterData(payload: any): CompanyMasterData {
  const companyMasterData = new CompanyMasterData();
  payload.IsStaging ? companyMasterData.companyInStaging() : companyMasterData.companyProcessed();
  companyMasterData.setFiscalNumber(payload.Company.FiscalNumber.Value);
  companyMasterData.setCompanyInfo(parseCompanyMasterDataInfo(payload.Company.CompanyInfo));
  companyMasterData.setGeoLocation(parseGeoLocation(payload.Company.GeoLocation));
  companyMasterData.setFederalOfficerInfo(parseFederalOfficerInfo(payload.Company.FederalOfficerInfo));
  companyMasterData.setCorporateName(payload.Company.CorporateName ? payload.Company.CorporateName : "");
  companyMasterData.setTradingName(payload.Company.TradingName ? payload.Company.TradingName : "");
  companyMasterData.setContract(payload.Company.Contract ? payload.Company.Contract : "");
  companyMasterData.setBranch(payload.Company.Branch ? payload.Company.Branch : "");
  companyMasterData.setSubBranch(payload.Company.SubBranch ? payload.Company.SubBranch : "");
  companyMasterData.setType(payload.Company.Type ? payload.Company.Type : "");
  companyMasterData.setDrugFront(payload.Company.DrugFront ? payload.Company.DrugFront : "");
  companyMasterData.setCluster(payload.Company.Cluster ? payload.Company.Cluster : "");
  companyMasterData.setSituation(payload.Company.Situation ? payload.Company.Situation : "");
  return companyMasterData;
}

export function parseCompanyMasterDataInfo(payload: any): CompanyMasterDataInfo {
  const companyInfo = new CompanyMasterDataInfo();
  if (!!payload) {
    companyInfo.setAddress(parseAddressMasterDataList(payload.Address));
    companyInfo.setEmais(payload.Emails);
    companyInfo.setTelephones(payload.Telephones);
  } else {
    companyInfo.setAddress(parseAddressMasterDataList(null));
    companyInfo.setEmais([]);
    companyInfo.setTelephones([]);
  }
  return companyInfo;
}

export function parseAddressMasterDataList(payload: any): AddressMasterData[] {
  let addressList: AddressMasterData[];
  if (!!payload) {
    addressList = payload.map((item) => {
      const address = new AddressMasterData();
      address.setIsNewAddress(item.IsNewAddress ? item.isNewAddress : false);
      address.setStreetType(item.StreetType ? item.StreetType : "");
      address.setStreet(item.Street ? item.Street : "");
      address.setNumber(item.Number ? item.Number : "");
      address.setComplement(item.Complement ? item.Complement : "");
      address.setZipCode(item.ZipCode ? item.ZipCode : "");
      address.setDistrict(item.District ? item.District : "");
      address.setCity(item.City ? item.City : "");
      address.setState(item.State ? item.State : "");
      return address;
    });
  } else {
    const address = new AddressMasterData();
    address.setIsNewAddress(false);
    address.setStreetType("");
    address.setStreet("");
    address.setNumber("");
    address.setComplement("");
    address.setZipCode("");
    address.setDistrict("");
    address.setCity("");
    address.setState("");
    addressList.push(address);
  }
  return addressList;
}

export function parseGeoLocation(payload: any): GeoLocation {
  const geoLocation = new GeoLocation();
  if (!!payload) {
    geoLocation.setLatitude(payload.Latitude ? payload.Latitude : "");
    geoLocation.setLongitude(payload.Longitude ? payload.Longitude : "");
  } else {
    geoLocation.setLatitude(0);
    geoLocation.setLongitude(0);
  }
  return geoLocation;
}

export function parseFederalOfficerInfo(payload: any): FederalOfficerInfo {
  const companyFederalOfficerInfo = new FederalOfficerInfo();
  if (!!payload) {
    companyFederalOfficerInfo.setFiscalNumberRoot(payload.FiscalNumberRoot ? payload.FiscalNumberRoot : "");
    companyFederalOfficerInfo.setConditionDate(payload.ConditionDate ? payload.ConditionDate : "");
    companyFederalOfficerInfo.setCompanySize(payload.CompanySize ? payload.CompanySize : "");
    companyFederalOfficerInfo.setOpened(payload.Opened ? payload.Opened : "");
    companyFederalOfficerInfo.setLegalNature(payload.LegalNature ? payload.LegalNature : "");
    companyFederalOfficerInfo.setLastUpdated(payload.LastUpdated ? payload.LastUpdated : "");
    companyFederalOfficerInfo.setStatus(payload.Status ? payload.Status : "");
    companyFederalOfficerInfo.setStatusMessage(payload.StatusMessage ? payload.StatusMessage : "");
    companyFederalOfficerInfo.setResponsibleFederalEntity(payload.ResponsibleFederalEntity ? payload.ResponsibleFederalEntity : "");
    companyFederalOfficerInfo.setConditionReason(payload.ConditionReason ? payload.ConditionReason : "");
    companyFederalOfficerInfo.setSpecialCondition(payload.SpecialCondition ? payload.SpecialCondition : "");
    companyFederalOfficerInfo.setSpecialConditionDate(payload.SpecialConditionDate ? payload.SpecialConditionDate : "");
    companyFederalOfficerInfo.setShareCapital(payload.ShareCapital ? payload.ShareCapital : "");
    companyFederalOfficerInfo.setBilling(parseBillingInfo(payload.Billing));
    companyFederalOfficerInfo.setActivities(parseActivitiesInfo(payload.Activities));
    companyFederalOfficerInfo.setSocialPartners(parseSocialPartnerInfo(payload.SocialPartners));
  } else {
    companyFederalOfficerInfo.setFiscalNumberRoot("");
    companyFederalOfficerInfo.setConditionDate("");
    companyFederalOfficerInfo.setCompanySize("");
    companyFederalOfficerInfo.setOpened("");
    companyFederalOfficerInfo.setLegalNature("");
    companyFederalOfficerInfo.setLastUpdated("");
    companyFederalOfficerInfo.setStatus("");
    companyFederalOfficerInfo.setStatusMessage("");
    companyFederalOfficerInfo.setResponsibleFederalEntity("");
    companyFederalOfficerInfo.setConditionReason("");
    companyFederalOfficerInfo.setSpecialCondition("");
    companyFederalOfficerInfo.setSpecialConditionDate("");
    companyFederalOfficerInfo.setShareCapital("");
    companyFederalOfficerInfo.setBilling(parseBillingInfo(null));
    companyFederalOfficerInfo.setActivities(parseActivitiesInfo(null));
    companyFederalOfficerInfo.setSocialPartners(parseSocialPartnerInfo(null));
  }
  return companyFederalOfficerInfo;
}

export function parseBillingInfo(payload: any): CompanyFederalOfficerBillingInfo {
  const billingInfo = new CompanyFederalOfficerBillingInfo();
  if (!!payload) {
    billingInfo.setFree(payload.Free ? payload.Free : false);
    billingInfo.setDatabase(payload.Database ? payload.Database : false);
  } else {
    billingInfo.setFree(false);
    billingInfo.setDatabase(false);
  }
  return billingInfo;
}

export function parseActivitiesInfo(payload: any): CompanyFederalOfficerActivityInfo[] {
  let activitiesInfo: CompanyFederalOfficerActivityInfo[] = [];
  if (!!payload) {
    activitiesInfo = payload.map((activity) => {
      const activityInfo = new CompanyFederalOfficerActivityInfo();
      activityInfo.setCode(activity.Code ? activity.Code : "");
      activityInfo.setText(activity.Text ? activity.Text : "");

      return activityInfo;
    });
  }
  return activitiesInfo;
}

export function parseSocialPartnerInfo(payload: any): CompanyFederalOfficerSocialPartnerInfo[] {
  let socialPartnerInfoList: CompanyFederalOfficerSocialPartnerInfo[] = [];
  if (!!payload) {
    socialPartnerInfoList = payload.map((info) => {
      const socialPartnerInfo = new CompanyFederalOfficerSocialPartnerInfo();
      socialPartnerInfo.setWhat(payload.What ? payload.What : "");
      socialPartnerInfo.setName(payload.Name ? payload.Name : "");

      return socialPartnerInfo;
    });
  }
  return socialPartnerInfoList;
}

export function parseJobDescription(payload: any): JobDescription {
  return new JobDescription(payload.Key.Value, payload.Title, payload.IsGeneric, payload.Enabled);
}

export function parseCompanyRemainingLimit(payload: any): CompanyRemainingLimit {
  const companyRemainingLimit = new CompanyRemainingLimit();

  if (payload) {
    companyRemainingLimit.setFiscalNumber(payload.FiscalNumber);
    companyRemainingLimit.setRemainingLimitValue(payload.RemainingLimitValue);
  }
  return companyRemainingLimit;
}
